import { Button, ButtonProps } from '@crono-one/design-system';
import { CSSProperties, useEffect, useRef, useState } from 'react';

type ExpandableTextToggleProps = Pick<
  ButtonProps,
  'variant' | 'tone' | 'shape' | 'size' | 'className'
>;

interface ExpandableTextProps {
  text?: string | null;
  lines?: number;
  className?: string;
  wrapperClassName?: string;
  showMoreLabel?: string;
  showLessLabel?: string;
  toggleProps?: ExpandableTextToggleProps;
  blockToggleOnExpanded?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

export const ExpandableText = ({
  text,
  lines = 1,
  className,
  wrapperClassName,
  showMoreLabel = 'Show more',
  showLessLabel = 'Show less',
  toggleProps: initialToggleProps,
  blockToggleOnExpanded = false,
  expanded,
  onExpandedChange,
}: ExpandableTextProps) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isControlled = expanded !== undefined;
  const isExpanded = isControlled ? expanded : internalExpanded;
  const [hasOverflow, setHasOverflow] = useState(false);

  const textRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const node = textRef.current;
    if (node === null) {
      return;
    }

    const measure = () => {
      if (!isExpanded && lines === 1) {
        setHasOverflow(node.scrollWidth > node.clientWidth);
      } else {
        setHasOverflow(node.scrollHeight > node.clientHeight);
      }
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(node);

    return () => observer.disconnect();
  }, [text, isExpanded, lines]);

  if (!text) {
    return null;
  }

  const toggle = (ev: React.MouseEvent) => {
    ev.stopPropagation();
    ev.preventDefault();
    const next = !isExpanded;
    if (!isControlled) {
      setInternalExpanded(next);
    }
    onExpandedChange?.(next);
  };

  const getClampStyle = (): CSSProperties | undefined => {
    if (isExpanded) return undefined;
    if (lines === 1) {
      return {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      };
    }
    return {
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: lines,
      overflow: 'hidden',
    };
  };

  const clampStyle = getClampStyle();

  const toggleProps = getToggleProps(initialToggleProps);

  if (isExpanded) {
    if (blockToggleOnExpanded) {
      return (
        <div
          className={`flex flex-col items-start gap-1 ${wrapperClassName ?? ''}`}
        >
          <p
            ref={textRef}
            className={`whitespace-pre-wrap break-words ${className ?? ''}`}
          >
            {text}
          </p>
          <Button {...toggleProps} onClick={toggle}>
            {showLessLabel}
          </Button>
        </div>
      );
    }

    return (
      <p
        ref={textRef}
        className={`whitespace-pre-wrap break-words ${className ?? ''}`}
      >
        {text}
        {(hasOverflow || isExpanded) && (
          <Button
            {...toggleProps}
            className={`inline h-5 ml-1 ${toggleProps.className ?? ''}`}
            onClick={toggle}
          >
            {showLessLabel}
          </Button>
        )}
      </p>
    );
  }

  return (
    <div className={`flex items-center gap-1 ${wrapperClassName ?? ''}`}>
      <p
        ref={textRef}
        className={`min-w-0 flex-1 whitespace-pre-wrap break-words ${
          className ?? ''
        }`}
        style={clampStyle}
      >
        {text}
      </p>

      {hasOverflow && (
        <Button
          {...toggleProps}
          className={`shrink-0 h-5 ${toggleProps.className ?? ''}`}
          onClick={toggle}
        >
          {showMoreLabel}
        </Button>
      )}
    </div>
  );
};

const getToggleProps: (
  initialProps?: ExpandableTextToggleProps,
) => ExpandableTextToggleProps = (initialProps) => {
  return {
    variant: 'text',
    tone: 'primary',
    ...initialProps,
  };
};
